import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle,
  Building2,
  Star,
  MapPin,
  Shield,
  Calendar,
  Users,
  Eye,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFormContext } from 'react-hook-form';

export function PropertyConfirmation() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { watch } = useFormContext();

  const name = watch('name');
  const type = watch('type');
  const stars = watch('stars');
  const location = watch('location');
  const photo = watch('photo');
  const contractScope = watch('contractScope');
  const startDate = watch('contractStartDate');
  const endDate = watch('contractEndDate');
  const selectedDepartments = watch('selectedDepartments') || [];
  const selectedTouchpoints = watch('selectedTouchpoints') || [];

  const toTitleCase = (s?: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

  return (
    <div className="space-y-6">
      <Card className="border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800">
        <CardContent className="pt-6">
          <div
            className={cn(
              'flex items-center gap-3',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            <div>
              <h3
                className={cn(
                  'text-lg font-semibold text-green-800 dark:text-green-200',
                  isRTL ? 'text-right' : 'text-left'
                )}
              >
                {t('properties.create.confirmation.title')}
              </h3>
              <p
                className={cn(
                  'text-green-700 dark:text-green-300',
                  isRTL ? 'text-right' : 'text-left'
                )}
              >
                {t('properties.create.confirmation.description')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle
            className={cn(
              'flex items-center gap-2',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <Building2 className="w-5 h-5" />
            {t('properties.create.confirmation.propertyInfo.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className={cn(
              'flex items-start gap-3',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <p className="font-medium">{name}</p>
              <p className="text-sm text-muted-foreground">
                {t('properties.create.confirmation.propertyInfo.name')}
              </p>
            </div>
          </div>

          {photo && (
            <div
              className={cn(
                'flex items-start gap-3',
                isRTL ? 'flex-row-reverse' : 'flex-row'
              )}
            >
              <div className="w-5 h-5 mt-0.5 flex items-center justify-center">
                <div className="w-3 h-3 bg-muted-foreground rounded-full" />
              </div>
              <div className={isRTL ? 'text-right' : 'text-left'}>
                <div className="mb-2">
                  <img
                    src={URL.createObjectURL(photo)}
                    alt={t(
                      'properties.create.confirmation.propertyInfo.photoAlt'
                    )}
                    className="w-24 h-24 object-cover rounded-lg border"
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('properties.create.confirmation.propertyInfo.photo')}
                </p>
              </div>
            </div>
          )}

          <div
            className={cn(
              'flex items-start gap-3',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <Badge variant="outline" className="gap-1">
              <Building2 className="w-3 h-3" />
              {toTitleCase(type)}
            </Badge>
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <p className="text-sm text-muted-foreground">Property Type</p>
            </div>
          </div>

          <div
            className={cn(
              'flex items-start gap-3',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <Star className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <p className="font-medium">
                {stars} Star{stars !== 1 ? 's' : ''}
              </p>
              <p className="text-sm text-muted-foreground">Star Rating</p>
            </div>
          </div>

          <div
            className={cn(
              'flex items-start gap-3',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <p className="font-medium">{location}</p>
              <p className="text-sm text-muted-foreground">Location</p>
            </div>
          </div>

          <div className="border-t pt-4 mt-6">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Contract Configuration
            </h4>

            <div className="space-y-3">
              <div
                className={cn(
                  'flex items-start gap-3',
                  isRTL ? 'flex-row-reverse' : 'flex-row'
                )}
              >
                <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <p className="font-medium">
                    {startDate
                      ? new Date(startDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'Not set'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Contract Start Date
                  </p>
                </div>
              </div>

              <div
                className={cn(
                  'flex items-start gap-3',
                  isRTL ? 'flex-row-reverse' : 'flex-row'
                )}
              >
                <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <p className="font-medium">
                    {endDate
                      ? new Date(endDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'Not set'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Contract End Date
                  </p>
                </div>
              </div>

              <div
                className={cn(
                  'flex items-start gap-3',
                  isRTL ? 'flex-row-reverse' : 'flex-row'
                )}
              >
                <Eye className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge
                      variant={
                        contractScope === 'full' ? 'default' : 'secondary'
                      }
                    >
                      {contractScope === 'full'
                        ? 'Full Access'
                        : 'Limited Access'}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">Access Scope</p>
                </div>
              </div>

              {contractScope === 'limited' && (
                <>
                  {selectedDepartments.length > 0 && (
                    <div
                      className={cn(
                        'flex items-start gap-3',
                        isRTL ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      <Users className="w-5 h-5 text-muted-foreground mt-0.5" />
                      <div className={isRTL ? 'text-right' : 'text-left'}>
                        <div className="flex flex-wrap gap-1 mb-1">
                          {selectedDepartments.map(
                            (dept: string, index: number) => (
                              <Badge
                                key={index}
                                variant="outline"
                                className="text-xs"
                              >
                                {dept}
                              </Badge>
                            )
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Selected Departments
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedTouchpoints.length > 0 && (
                    <div
                      className={cn(
                        'flex items-start gap-3',
                        isRTL ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      <Users className="w-5 h-5 text-muted-foreground mt-0.5" />
                      <div className={isRTL ? 'text-right' : 'text-left'}>
                        <div className="flex flex-wrap gap-1 mb-1">
                          {selectedTouchpoints.map(
                            (tp: string, index: number) => (
                              <Badge
                                key={index}
                                variant="outline"
                                className="text-xs"
                              >
                                {tp}
                              </Badge>
                            )
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Selected Touchpoints
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {startDate && endDate && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <strong>Contract Duration:</strong>{' '}
                    {Math.ceil(
                      (new Date(endDate).getTime() -
                        new Date(startDate).getTime()) /
                        (1000 * 60 * 60 * 24)
                    )}{' '}
                    days
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
